import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { completeMissionApi } from '../../common/api'
import { getMission } from '../../common/state/mission/action'
import {
  setOpenNextMissionIntroVideoPending,
  setShowMissionCompleteNotification,
} from '../../common/state/mission/reducer'
import { RootState, useAppDispatch } from '../../store'
import { NOTIFICATION_ANIMATION_MS } from './missionCompleteConstants'
import { MissionStarBurst } from './MissionStarBurst'

const MISSION_COMPLETED_IMAGE = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/_merged_/story-mission/mission-completed-notification.webp'

export const MissionCompleteNotification: React.FC = () => {
  const { t } = useTranslation()
  const show = useSelector(
    (state: RootState) => state.app.mission.showMissionCompleteNotification
  )
  const pendingMissionDay = useSelector(
    (state: RootState) => state.app.mission.pendingMissionComplete
  )
  const dispatch = useAppDispatch()
  const [exiting, setExiting] = React.useState(false)
  const [entered, setEntered] = React.useState(false)
  const [burstKey, setBurstKey] = React.useState(0)
  const exitTransitionDoneRef = React.useRef(false)
  const pendingCompleteDayRef = React.useRef<number | null>(null)

  const requestClose = React.useCallback(() => {
    pendingCompleteDayRef.current = pendingMissionDay
    setExiting(true)
  }, [pendingMissionDay])

  React.useEffect(() => {
    if (!show) {
      setEntered(false)
      setExiting(false)
      exitTransitionDoneRef.current = false
      return
    }
    exitTransitionDoneRef.current = false
    setEntered(false)
    const enterId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setEntered(true)
        setBurstKey((k) => k + 1)
      })
    })
    return () => {
      cancelAnimationFrame(enterId)
    }
  }, [show])

  React.useEffect(() => {
    if (!show || !entered || exiting) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [show, entered, exiting, requestClose])

  const handleTransitionEnd = React.useCallback(
    (e: React.TransitionEvent) => {
      if (!exiting) return
      if (e.propertyName !== 'transform' && e.propertyName !== 'opacity') return
      if (exitTransitionDoneRef.current) return
      exitTransitionDoneRef.current = true
      const day = pendingCompleteDayRef.current
      pendingCompleteDayRef.current = null
      if (day != null) {
        void (async () => {
          try {
            await completeMissionApi(day)
            await dispatch(getMission({})).unwrap()
            dispatch(setOpenNextMissionIntroVideoPending(true))
          } catch (_) {
            try {
              await dispatch(getMission({})).unwrap()
            } catch {
              /* ignore */
            }
          }
        })()
      }
      dispatch(setShowMissionCompleteNotification(false))
      setExiting(false)
    },
    [exiting, dispatch]
  )

  if (!show && !exiting) return null
  const isOffScreen = !entered || exiting
  const clickAnywhereHint = t('main.missionModal.missionCompleteClickAnywhere', {
    defaultValue: 'Click anywhere,\nto go to the next mission',
  })

  return (
    <>
      <button
        type="button"
        className={`fixed inset-0 z-[199] cursor-default border-0 bg-black/70 p-0 transition-opacity duration-200 ${
          isOffScreen ? 'pointer-events-none' : 'pointer-events-auto'
        }`}
        style={{ opacity: isOffScreen ? 0 : 1 }}
        onClick={requestClose}
        aria-label={clickAnywhereHint}
      />
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 pointer-events-none"
        aria-live="polite"
        role="presentation"
      >
        <div
          className="pointer-events-auto flex cursor-pointer flex-col items-center"
          role="dialog"
          aria-modal="true"
          aria-describedby="mission-complete-hint"
          aria-labelledby="mission-complete-title"
          onClick={requestClose}
        >
          <div
            onTransitionEnd={handleTransitionEnd}
            className="flex flex-col items-center transition-[transform,opacity] duration-300 ease-out"
            style={{
              transform: isOffScreen ? 'scale(0.9)' : 'scale(1)',
              opacity: isOffScreen ? 0 : 1,
              transitionDuration: `${NOTIFICATION_ANIMATION_MS}ms`,
              willChange: 'transform, opacity',
            }}
          >
            <div className="relative">
              <div
                className="relative z-10 [filter:drop-shadow(0_0_14px_rgba(255,255,255,0.75))_drop-shadow(0_0_36px_rgba(255,255,255,0.45))_drop-shadow(0_0_72px_rgba(255,255,255,0.22))_drop-shadow(0_0_110px_rgba(255,255,255,0.08))]"
              >
                <img
                  src={MISSION_COMPLETED_IMAGE}
                  alt=""
                  className="h-auto w-auto object-contain
                    max-h-[min(44vh,360px)] max-w-[min(94vw,480px)]
                    sm:max-h-[min(54vh,520px)] sm:max-w-[min(92vw,840px)]
                    md:max-h-[min(60vh,600px)] md:max-w-[min(90vw,1080px)]
                    lg:max-w-[min(88vw,1180px)]"
                />
                {/* `top` % is of the image box height (scales with object-contain). Padding % would use width, not height. */}
                <div
                  className="pointer-events-none absolute left-1/2 top-[12.9%] z-[11] flex w-[82%] max-w-[min(82vw,960px)] -translate-x-1/2 justify-center sm:top-[13.5%] md:top-[14%] lg:top-[14.5%] sm:w-[80%]"
                >
                  <span
                    id="mission-complete-title"
                    className="mission-complete-title-text block text-center text-base font-bold leading-tight tracking-wide sm:text-xl md:text-2xl lg:text-[1.65rem]"
                  >
                    Mission Completed!
                  </span>
                </div>
                {/* `bottom` % is of the image box height (same as title `top` %). Sits in the lower dark band, not flush with the frame edge. */}
                <div className="pointer-events-none absolute left-1/2 bottom-[16%] z-[11] flex w-full max-w-[92%] -translate-x-1/2 justify-center px-2 sm:bottom-[17%] md:bottom-[18%] lg:bottom-[19%]">
                  <p
                    id="mission-complete-hint"
                    className="whitespace-pre-line text-center text-xs leading-snug text-[#f0e8d8]/95 sm:text-sm md:text-base [font-family:'Tilt_Warp',sans-serif] [text-shadow:0_1px_3px_rgba(0,0,0,0.85),0_0_8px_rgba(0,0,0,0.45)]"
                  >
                    {clickAnywhereHint}
                  </p>
                </div>
              </div>
              <style>{`
            .mission-complete-title-text {
              background: linear-gradient(
                105deg,
                #fef3c7 0%,
                #fde68a 15%,
                #fcd34d 35%,
                #fef9c3 50%,
                #fcd34d 65%,
                #fde68a 85%,
                #fef3c7 100%
              );
              background-size: 200% 100%;
              -webkit-background-clip: text;
              background-clip: text;
              color: transparent;
              filter: drop-shadow(0 0 2px rgba(0,0,0,0.35)) drop-shadow(0 1px 2px rgba(0,0,0,0.3)) drop-shadow(0 0 12px rgba(251,191,36,0.5));
              animation: mission-complete-text-shine 2.5s linear infinite;
            }
            @keyframes mission-complete-text-shine {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}</style>
              <MissionStarBurst burstKey={burstKey} visible={entered && !exiting} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
